import CircularLoading from '@/components/circular-loading';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { ParentAccountOrderDto } from '@/models/suba/parent-account-orders/ParentAccountOrderDTO.model';
import ParentAccountOrderService from '@/services/suba/parent-account-orders/ParentAccountOrder.service';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';

import { ParentAccountOrderInfoBanner } from './ParentAccountOrderInfoBanner.component';

type RejectParentAccountOrderDialogProps = {
  open: boolean;
  parentAccountOrder: ParentAccountOrderDto;
  onClose: () => void;
  onCancel: () => void;
  onReject: () => void;
};

export const RejectParentAccountOrderDialog: FC<
  RejectParentAccountOrderDialogProps
> = props => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const rejectParentAccountOrderMutation = useMutation(
    () => ParentAccountOrderService.reject(props.parentAccountOrder.id),
    {
      onError: () => {
        showSnackbar({
          message: `Failed to manually reject parent account order ${props.parentAccountOrder.ourParentOrderId}`,
          severity: 'error'
        });
      },
      onSuccess: () => {
        showSnackbar({
          message: `The selected order has been manually rejected.`,
          severity: 'success'
        });

        queryClient.invalidateQueries({
          queryKey: [
            'ParentAccountService.getById',
            props.parentAccountOrder.id
          ]
        });

        queryClient.invalidateQueries({
          queryKey: ['ParentAccountOrderService.search']
        });

        props.onReject();
      }
    }
  );

  const getParentAccountOrderQuery = useQuery(
    ['ParentAccountOrderService.getById', props.parentAccountOrder.id],
    () => ParentAccountOrderService.getById(props.parentAccountOrder.id)
  );

  return (
    <Dialog fullWidth maxWidth='md' onClose={props.onClose} open={props.open}>
      <DialogTitle>Manually Reject Parent Account Order</DialogTitle>
      <ParentAccountOrderInfoBanner
        parentAccountOrderId={props.parentAccountOrder.id}
      />
      <DialogContent>
        {getParentAccountOrderQuery.isInitialLoading ? (
          <CircularLoading size='40px' />
        ) : (
          <Typography>
            Manually rejecting this order will update this Parent Account Order
            and the related Sub Account Orders to REJECTED. This update will
            only be processed internally and no action will be taken at the
            custodian and/or fund company. This order should only be manually
            rejected if it was already rejected at the custodian/fund company.
            Once the order is rejected, it can either be Deferred or
            Resubmitted.
          </Typography>
        )}
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction='row' m={1} spacing={2}>
          <Button data-testid='cancel-button' onClick={props.onCancel}>
            Cancel
          </Button>
          <LoadingButton
            color='error'
            data-testid='reject-button'
            disabled={getParentAccountOrderQuery.isInitialLoading}
            loading={rejectParentAccountOrderMutation.isLoading}
            onClick={() => rejectParentAccountOrderMutation.mutate()}
            variant='contained'>
            Reject
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
